import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { isInAppBrowser, inAppName, isIOS, canJumpToBrowser, openInExternalBrowser } from '../utils/inAppBrowser';
import { withVisitorId } from '../utils/abTest';
import './OpenInBrowserPrompt.css';

// Pop-up shown when someone taps "Continue with Google" inside the Facebook /
// Instagram in-app browser, where Google blocks sign-in: offers to open the
// page in their phone's browser, or carry on with email. Some apps (Facebook
// on iPhone) block that jump, so if the page is still showing a moment later
// it switches to the app-menu steps and a copyable link (straight away on
// iPhones too old for the jump).
const OpenInBrowserPrompt = ({ url, emailLabel = 'Sign up with email instead', onEvent = () => {}, onClose }) => {
  // Older iPhones (iOS 16 and below) can't jump to Safari — go straight to the steps.
  const [stuck, setStuck] = useState(() => !canJumpToBrowser());
  useEffect(() => { if (stuck) onEvent('sheet_steps'); }, [stuck]); // eslint-disable-line react-hooks/exhaustive-deps
  const [copied, setCopied] = useState(null); // null | true | false (couldn't — show the link)
  const app = inAppName();
  const ios = isIOS();
  // Carries their visitor ID, so the visit in their own browser links up
  // with this one in analytics.
  const link = withVisitorId(url || window.location.href);

  const open = () => {
    onEvent('sheet_open_browser');
    let left = false;
    const onHide = () => { if (document.visibilityState === 'hidden') left = true; };
    document.addEventListener('visibilitychange', onHide);
    openInExternalBrowser(link);
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onHide);
      if (!left && document.visibilityState === 'visible') setStuck(true);
    }, 1200);
  };

  const copy = async () => {
    onEvent('sheet_copy_link');
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return createPortal(
    <div className="oib-overlay" onClick={onClose}>
      <div className="oib-box" role="dialog" aria-modal="true" aria-labelledby="oib-title" onClick={(e) => e.stopPropagation()}>
        {!stuck ? (<>
          <h2 className="oib-title" id="oib-title">Open in your phone&apos;s browser to sign in with Google</h2>
          <p className="oib-text">
            Google sign-in doesn&apos;t work inside {app === 'this app' ? "this app's" : `${app}'s`} browser.
          </p>
          <button type="button" className="oib-btn oib-btn--primary" onClick={open}>
            Open in browser
          </button>
          <p className="oib-hint">Or tap <strong>•••</strong> at the top, then &quot;Open in browser&quot;.</p>
        </>) : (<>
          {canJumpToBrowser() ? (
            <h2 className="oib-title" id="oib-title">Open it from the menu instead</h2>
          ) : (<>
            <h2 className="oib-title" id="oib-title">Open in your phone&apos;s browser to sign in with Google</h2>
            <p className="oib-text">
              Google sign-in doesn&apos;t work inside {app === 'this app' ? "this app's" : `${app}'s`} browser.
            </p>
          </>)}
          <ol className="oib-steps">
            <li>Tap <strong>•••</strong> at the top right of this screen</li>
            <li>Tap <strong>&quot;Open in external browser&quot;</strong>{ios ? '' : ' (or "Open in Chrome")'}</li>
            <li>Then tap <strong>Continue with Google</strong> again</li>
          </ol>
          <button type="button" className="oib-btn oib-btn--primary" onClick={copy}>
            {copied ? `Link copied — paste it into ${ios ? 'Safari' : 'your browser'}` : 'Or copy the link'}
          </button>
          {copied === false && (
            <input className="oib-link" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Link to this page" />
          )}
        </>)}
        <button type="button" className="oib-btn oib-btn--plain" onClick={() => { onEvent('sheet_email_instead'); onClose(); }}>
          {emailLabel}
        </button>
      </div>
    </div>,
    document.body
  );
};

// For a "Continue with Google" button: guard(handler) runs the handler as
// normal, but inside an in-app browser shows the pop-up instead. ?iabtest=1
// skips it, to check what Google itself does there. emailLabel: e.g. "Log in
// with email instead" on login pages. onEvent(step) reports what they do in
// the sheet (sign-up page analytics).
export const useInAppGoogleGuard = (url, emailLabel, onEvent = () => {}) => {
  const [open, setOpen] = useState(false);
  const guard = (handler) => (...args) => {
    if (isInAppBrowser() && !new URLSearchParams(window.location.search).has('iabtest')) {
      if (args[0]?.preventDefault) args[0].preventDefault();
      onEvent('google_blocked');
      setOpen(true);
      return undefined;
    }
    return handler(...args);
  };
  const prompt = open ? <OpenInBrowserPrompt url={typeof url === 'function' ? url() : url} emailLabel={emailLabel} onEvent={onEvent} onClose={() => setOpen(false)} /> : null;
  return { guard, prompt };
};

export default OpenInBrowserPrompt;
